import { PlayArrow } from '@mui/icons-material';
import { Button, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import React, { ReactNode, useEffect, useState } from "react";
import { IAvailableList, IAvailableListItem, IShortageList, IShortageListFilter, IShortageListItem } from '../../routes/doa-forecast-doa-admin-stats/DoaForecastStatsData';
import { StatusType } from '../../routes/doa-forecast-doa-admin-stats/DoaForecastStatsModels';
import { DetailsItem } from "../../routes/doa-forecast-stats/DoaForecastStatsData";
import { DoaForecastList } from "../../routes/doa-forecast-stats/DoaForecastStatsModels";
import { PlateModal } from '../PlateModal';
import ScrollIndicatorsComp from '../ScrollIndicatorsComp/ScrollIndicatorsComp';
import { AvailableTable } from './AvailableTable';
import { JobsRequiredTable } from './JobsRequiredTable';
import { ShortagesTable } from './ShortagesTable';
import { forecastTableStyle } from "./styled";

interface IForecastCollapsibleTableProps {
  availableLists?: IAvailableList[],
  shortageLists?: IShortageList[],
  detail: DetailsItem,
  filter?: IShortageListFilter,
  loading: boolean,
  submitting?: boolean,
  onLoadList?: (list: DoaForecastList, jobId: number, flag545: number) => void;
  onFilterUpdate?: (updatedFilter: IShortageListFilter) => void;
  onUpdateCandidateStatuses?: (
    jobId: number,
    flag545: number,
    boardCode: string,
    regId: number,
    statusType: StatusType,
    status: boolean
  ) => void;
  isDoaAdmin: boolean,
  isFulfilled?: boolean
}

export const ForecastCollapsibleTable: React.FC<IForecastCollapsibleTableProps> = ({
  onLoadList,
  onFilterUpdate,
  onUpdateCandidateStatuses,
  loading,
  submitting,
  detail,
  filter,
  availableLists,
  shortageLists,
  isDoaAdmin,
  isFulfilled
}) => {
  const [openRows, setOpenRows] = useState<{ rowId: number, cellId: 1 | 2 | 5 }[]>([]);
  const [expandedLists, setExpandedLists] = useState<{ rowId: number, list: null | DoaForecastList }[]>([]);
  const [regId, setRegId] = useState(0);
  const [board, setBoard] = useState('');
  const [callbackInfo, setCallbackInfo] = useState<{ boardCode: string, regId: number }[]>([]);

  // reset expanded list and opened rows on refresh
  useEffect(() => {
    if (!loading && shortageLists?.length === 0 && availableLists?.length === 0) {
      setExpandedLists([]);
      setOpenRows([]);
    }
  }, [shortageLists, availableLists, loading])

  const onSetOpenCallbackDetails = (boardCode: string, regId: number) => {
    setCallbackInfo(ci => {
      const isExisted = ci.find(i => i.boardCode === boardCode && i.regId === regId);
      return isExisted ? ci.filter(i => i.boardCode !== boardCode || i.regId !== regId) :
        [...ci, { boardCode, regId }]
    })
  }

  const onArrowClick = (rowId: number, cellId: 1 | 2 | 5, list: DoaForecastList) => {
    setOpenRows((prev) => {
      const existingRow = prev.find((i) => i.rowId === rowId);
      const isExpanded = existingRow !== undefined;
      // if condition to deal with switching list types in an opened row
      if (isExpanded && existingRow.cellId !== cellId) {
        // the user tries switching to a shortage list, close all lists in other rows
        if (list === DoaForecastList.shortage) return [{ rowId, cellId }];
        // the user tries switching to another list, update the cellId for current row
        return prev.map((i) => (i.rowId === rowId ? { rowId, cellId } : i));
        // if condition to deal with opening and closing a list in a closed row
      } else {
        // if the user tries to open a shortage list, close all lists in other rows
        // if the user tries to close a shortage list, remove the row from the state
        if (list === DoaForecastList.shortage) {
          return isExpanded ? prev.filter((i) => i.rowId !== rowId) : [{ rowId, cellId }];
        }
        // if the user tries to open a list, add a record for current row
        // if the user tries to close a list, remove the row from the state
        return isExpanded ? prev.filter((i) => i.rowId !== rowId) : [...prev, { rowId, cellId }];
      }
    });

    if (onLoadList) {
      const jobStat = detail.jobStats[rowId];
      onLoadList(list, jobStat.jobId, jobStat.flag545);
    }

    setExpandedLists(prev => {
      const currList = prev.find(l => l.rowId === rowId);
      // if condition to deal with closing a list
      if (currList && currList.list === list) {
        // the user tries to close the opened list on the current row, remove the row from the state
        return prev.filter(l => l.rowId !== rowId);
        // if condition to deal with switching list types
      } else if (currList) {
        // the user tries switching to a shortage list, close all lists in other rows
        if (list === DoaForecastList.shortage) return [{ rowId, list }];
        // the user tries switching to another list, update the cellId for current row
        return prev.map(l => l.rowId === rowId ? ({ ...l, list }) : l);
        // if condition to deal with opening a list
      } else {
        // the user tries to open a shortage list, close all lists in other rows
        if (list === DoaForecastList.shortage) return [{ rowId, list }];
        // the user tries to open a list, add a record for the current row
        return [...prev, { rowId, list }];
      }
    })
  };

  const onOpenClosePlateModal = (regId = 0, board = '') => {
    setRegId(regId);
    setBoard(board);
  }

  const getExpandedTable = (rowId: number, redBorderClassName: string) => {
    const jobDetails = detail.jobStats[rowId].jobDetails;

    const jobRequiredTableProps = {
      isDoaAdmin, jobDetails, redBorderClassName,
      onClose: () => onArrowClick(rowId, 1, DoaForecastList.jobs)
    }

    const currentList = expandedLists.find(l => l.rowId === rowId);
    const jobStat = detail.jobStats[rowId];
    const isShortageList = currentList?.list === DoaForecastList.shortage;

    // Use type assertions to help TypeScript understand the type
    const list = isShortageList ?
      shortageLists?.find((l) => l.jobId === jobStat.jobId && l.flag545 === jobStat.flag545) :
      availableLists?.find((l) => l.jobId === jobStat.jobId && l.flag545 === jobStat.flag545);

    const table = isShortageList ?
      <ShortagesTable
        onUpdateCandidateStatuses={onUpdateCandidateStatuses!}
        list={list?.list as unknown as IShortageListItem[]}
        currentJobId={jobStat.jobId}
        currentFlag545={jobStat.flag545}
        boardOptions={(list as IShortageList)?.boardOptions}
        isDoaAdmin={isDoaAdmin}
        isFulfilled={isFulfilled || false}
        filter={filter!}
        loading={loading}
        submitting={submitting}
        redBorderClassName={redBorderClassName}
        onFilterUpdate={onFilterUpdate!}
        onOpenClosePlateModal={onOpenClosePlateModal}
        callbackInfo={callbackInfo}
        onSetOpenCallbackDetails={onSetOpenCallbackDetails}
        onClose={() => onArrowClick(rowId, 5, DoaForecastList.shortage)}
      /> :
      <AvailableTable
        list={list?.list as unknown as IAvailableListItem[]}
        isDoaAdmin={isDoaAdmin}
        redBorderClassName={redBorderClassName}
        loading={loading}
        onOpenClosePlateModal={onOpenClosePlateModal}
        onClose={() => onArrowClick(rowId, 2, DoaForecastList.available)}
      />;

    return currentList?.list === DoaForecastList.jobs ? <JobsRequiredTable {...jobRequiredTableProps} /> : table;
  }

  return (
    <ScrollIndicatorsComp>
      <Table size="custom" sx={forecastTableStyle}>
        <TableHead>
          <TableRow>
            {['Job Description', 'Jobs Req.', 'Total Avail.', 'Matched Avail.', 'TSC (+/-)', 'Short (+/-)'].map((c, i) => (
              <TableCell
                key={c}
                className={`${i === 1 ? 'red' : ''} ${i === 2 || i === 5 ? 'green' : ''}`}
                colSpan={(i === 1) || (i === 2 && isDoaAdmin) || (i === 5 && isDoaAdmin) ? 2 : 1}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.jobStats.map((r, ri) => {
            const redBorderClassName = `${detail.snapshotEndIndex !== null && ri <= detail.snapshotEndIndex ? 'redBorder' : ''}`
            return (
              <React.Fragment key={ri}>
                <TableRow
                  className={`
                ${redBorderClassName} 
                ${detail.snapshotStartIndex !== null && ri === detail.snapshotStartIndex ? 'topRedBorder' : ''}
                ${detail.snapshotEndIndex !== null && detail.snapshotEndIndex === ri ? 'bottomRedBorder' : ''}
                ${r.short < 0 ? 'blueBg' : ''}
              `}
                >
                  {
                    Object.values(r).slice(0, -3).map((c, ci) => {
                      const listType = ci === 2 ? DoaForecastList.available : DoaForecastList.shortage;
                      const list = ci === 1 ? DoaForecastList.jobs : listType;
                      const expanded = !!openRows.find(item => item.rowId === ri && item.cellId === ci);
                      const hasExpandRow = (ci === 1) || (ci === 2 && isDoaAdmin) || (ci === 5 && isDoaAdmin);
                      return (
                        <React.Fragment key={ci} >
                          <TableCell
                            className={`
                            ${ci === 1 ? 'red' : ''} 
                            ${ci === 2 || ci === 5 ? 'green' : ''}
                            ${expanded ? 'open' : ''}
                            ${hasExpandRow ? 'alignRight' : ''}
                            ${hasExpandRow && !!c ? 'expand' : ''}
                          `}
                            onClick={() => hasExpandRow && !!c ? onArrowClick(ri, ci, list) : undefined}
                          >
                            {c as ReactNode}
                          </TableCell>
                          {hasExpandRow && (
                            <TableCell className={`${expanded ? 'open' : ''} alignLeft`}>
                              <Button
                                variant="expand-icon"
                                aria-label="expand row"
                                size="small"
                                disabled={!c}
                                onClick={() => onArrowClick(ri, ci, list)}
                              >
                                <PlayArrow className={expanded ? 'open' : ''} />
                              </Button>
                            </TableCell>
                          )}
                        </React.Fragment>
                      )
                    })
                  }
                </TableRow>
                {!!openRows.find(r => r.rowId === ri) && getExpandedTable(ri, redBorderClassName)}
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
      {board && regId && <PlateModal
        onClose={() => onOpenClosePlateModal()}
        open={!!regId && !!board}
        board={board}
        id={Number(regId)}
      />}
    </ScrollIndicatorsComp>
  )
}